import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard,
  CreditCard,
  Calendar,
  Gift,
  BookOpen,
  MessageSquare,
  FileText,
  Receipt,
  Settings,
  User,
  LogOut,
  QrCode,
  Menu,
  X,
  Bell,
  ChevronRight,
  Radio,
  Shield,
  Video
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { LogoUpim } from '../ui-upim';

const sidebarLinks = [
  { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { name: 'Mi Membresía', path: '/dashboard/membresia', icon: CreditCard },
  { name: 'Credencial QR', path: '/dashboard/credencial', icon: QrCode },
  { name: 'Mensajes', path: '/dashboard/mensajes', icon: MessageSquare, badge: 2 },
  { name: 'Eventos', path: '/dashboard/eventos', icon: Calendar },
  { name: 'Beneficios', path: '/dashboard/beneficios', icon: Gift },
  { name: 'Academia', path: '/dashboard/academia', icon: BookOpen },
  { name: 'Documentos', path: '/dashboard/documentos', icon: FileText },
  { name: 'Mis datos fiscales', path: '/mis-datos-fiscales', icon: Receipt },
  { name: 'Mi Perfil', path: '/dashboard/perfil', icon: User },
];

// Links adicionales para admins
const adminLinks = [
  { name: 'Panel Admin', path: '/admin', icon: Shield },
  { name: 'Lives / Streaming', path: '/admin/lives', icon: Radio },
  { name: 'Biblioteca Videos', path: '/admin/videos', icon: Video },
];

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user is admin or superadmin
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const handleLogout = () => {
    logout();
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-black" data-testid="dashboard-shell">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 border-r border-[var(--upim-borde)] bg-[var(--upim-superficie-1)]
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex h-[64px] items-center justify-between border-b border-[var(--upim-borde)] px-4">
            <Link to="/" className="flex items-center gap-2.5" data-testid="dashboard-logo-enlace">
              <LogoUpim className="h-8" claseTexto="text-lg" testId="dashboard-logo" />
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Cerrar menú"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 border-b">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <User className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            {user?.membership && (
              <div className="mt-3">
                <Badge className={`
                  ${user.membership.status === 'active' ? 'bg-emerald-100 text-emerald-700' : ''}
                  ${user.membership.status === 'expiring' ? 'bg-amber-100 text-amber-700' : ''}
                  ${user.membership.status === 'expired' ? 'bg-red-100 text-red-700' : ''}
                `}>
                  {user.membership.status === 'active' && 'Membresía Activa'}
                  {user.membership.status === 'expiring' && `Vence en ${user.membership.daysRemaining} días`}
                  {user.membership.status === 'expired' && 'Membresía Vencida'}
                </Badge>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-1">
              {sidebarLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        relative flex items-center justify-between rounded-xl px-3 py-2.5 transition-colors cursor-pointer
                        ${isActive
                          ? 'bg-[var(--upim-acento-suave)] text-[var(--upim-texto)]'
                          : 'text-[var(--upim-texto-2)] hover:bg-[var(--upim-superficie-3)] hover:text-[var(--upim-texto)]'
                        }
                      `}
                    >
                      {isActive && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-[var(--upim-acento)]" aria-hidden="true" />}
                      <div className="flex items-center space-x-3">
                        <link.icon className={`h-5 w-5 ${isActive ? 'text-[var(--upim-acento)]' : ''}`} />
                        <span className="text-sm font-medium">{link.name}</span>
                      </div>
                      {link.badge && (
                        <Badge className="bg-red-500 text-white text-xs h-5 min-w-[20px] flex items-center justify-center">
                          {link.badge}
                        </Badge>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Admin Links - Only show for admin/superadmin */}
            {isAdmin && (
              <>
                <div className="my-4 border-t border-gray-200"></div>
                <p className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase">Administración</p>
                <ul className="space-y-1">
                  {adminLinks.map((link) => {
                    const isActive = location.pathname === link.path;
                    return (
                      <li key={link.path}>
                        <Link
                          to={link.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`
                            flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer
                            ${isActive
                              ? 'bg-purple-50 text-purple-700'
                              : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
                            }
                          `}
                        >
                          <link.icon className={`h-5 w-5 ${isActive ? 'text-purple-600' : ''}`} />
                          <span className="text-sm font-medium">{link.name}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5 mr-3" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="upim-vidrio sticky top-0 z-30" data-testid="dashboard-barra-superior">
          <div className="flex h-16 items-center justify-between px-4 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 hover:bg-gray-100 rounded-lg"
                aria-label="Abrir menú"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="ml-2 font-display text-lg font-bold text-[var(--upim-texto)] lg:ml-0">
                {sidebarLinks.find(l => l.path === location.pathname)?.name || 'Dashboard'}
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <button className="relative p-2 hover:bg-gray-100 rounded-lg" aria-label="Notificaciones">
                <Bell className="h-5 w-5 text-gray-600" />
                <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
              </button>
              <Link to="/" className="text-sm text-[var(--upim-texto-2)] transition-colors hover:text-[var(--upim-acento)]">
                Ir al sitio
                <ChevronRight className="inline h-4 w-4" />
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
